import { createSlice } from "@reduxjs/toolkit";
import { CareCommentThreadJson } from "../../../domain/care_comment/CareCommentThread";
import { deleteCareCommentAction, registerCareCommentAction, updateCareCommentAction } from "../admin/care_comment/actions";
import { ActionType, getCareCommentListAction, markAsReadCareCommentAction, pinCareCommentAction, resetCareCommentAction, unpinCareCommentAction } from "./actions";

export type CareCommentState = Readonly<{
    careCommentThreadList: CareCommentThreadJson[] | null
}>

const careCommentInitialState: CareCommentState = {
    careCommentThreadList: null
};

const slice = createSlice({
    name: ActionType.base,
    initialState: careCommentInitialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(
                getCareCommentListAction.pending,
                () => {}
            )
            .addCase(
                getCareCommentListAction.fulfilled,
                (state, action) => {
                    const { payload } = action;
                    const { careCommentThreadList } = payload;
                    state.careCommentThreadList = careCommentThreadList;
                }
            )
            .addCase(
                getCareCommentListAction.rejected,
                () => {}
            )
            .addCase(
                resetCareCommentAction,
                (state, _) => {
                    state.careCommentThreadList = null;
                }
            )
            .addCase(
                markAsReadCareCommentAction.fulfilled,
                (state, action) => {
                    const readCareCommentIdList = action.payload.careCommentIdList;

                    const newCareCommentThreadList = state.careCommentThreadList?.map(careCommentThread => ({
                        ...careCommentThread,
                        careCommentList: careCommentThread.careCommentList.map(careComment => ({
                            ...careComment,
                            isRead: careComment.isRead || readCareCommentIdList.includes(careComment.id)
                        }))
                    })) ?? null;

                    state.careCommentThreadList = newCareCommentThreadList;
                }
            )
            .addCase(
                pinCareCommentAction.fulfilled,
                (state, action) => {
                    const pinnedCareCommentThreadId = action.payload.careCommentThreadId;

                    const newCareCommentThreadList = state.careCommentThreadList?.map(careCommentThread => ({
                        ...careCommentThread,
                        isPinned: careCommentThread.isPinned || careCommentThread.id === pinnedCareCommentThreadId
                    })) ?? null;

                    state.careCommentThreadList = newCareCommentThreadList;
                }
            )
            .addCase(
                unpinCareCommentAction.fulfilled,
                (state, action) => {
                    const unpinnedCareCommentThreadId = action.payload.careCommentThreadId;

                    const newCareCommentThreadList = state.careCommentThreadList?.map(careCommentThread => ({
                        ...careCommentThread,
                        isPinned: careCommentThread.isPinned && careCommentThread.id !== unpinnedCareCommentThreadId
                    })) ?? null;

                    state.careCommentThreadList = newCareCommentThreadList;
                }
            )
            .addCase(
                registerCareCommentAction.fulfilled,
                (state, action) => {
                    const { carecomment } = action.payload;

                    const newCareCommentThreadList = state.careCommentThreadList?.map(careCommentThread => ({
                        ...careCommentThread,
                        careCommentList: careCommentThread.id === carecomment.threadId ? [
                            ...careCommentThread.careCommentList,
                            carecomment
                        ] : careCommentThread.careCommentList
                    })) ?? null;

                    state.careCommentThreadList = newCareCommentThreadList;
                }
            )
            .addCase(
                updateCareCommentAction.fulfilled,
                (state, action) => {
                    const newCareComment = action.payload.carecomment;

                    const newCareCommentThreadList = state.careCommentThreadList?.map(careCommentThread => ({
                        ...careCommentThread,
                        careCommentList: careCommentThread.careCommentList.map(careComment => (
                            careComment.id === newCareComment.id ? {
                                ...careComment,
                                title: newCareComment.title,
                                content: newCareComment.content
                            } : careComment
                        ))
                    })) ?? null;

                    state.careCommentThreadList = newCareCommentThreadList;
                }
            )
            .addCase(
                deleteCareCommentAction.fulfilled,
                (state, action) => {
                    const deletedCareCommentId = action.payload.careCommentId;

                    const newCareCommentThreadList = state.careCommentThreadList?.map(careCommentThread => ({
                        ...careCommentThread,
                        careCommentList: careCommentThread.careCommentList.filter(careComment => careComment.id !== deletedCareCommentId)
                    })).filter(careCommentThread => careCommentThread.careCommentList.length) ?? null;

                    state.careCommentThreadList = newCareCommentThreadList;
                }
            );
    } 
});

export const careCommentReducer = slice.reducer;
